import { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import ProjectGrid from './components/ProjectGrid';
import AboutSection from './components/AboutSection';
import Footer from './components/Footer';
import CustomCursor from '../../components/CustomCursor';

const FILTER_TAGS = [
  'API Design',
  'UI/UX',
  'Merchandising',
  'Branding',
  'AI/ML',
  'Data Visualization',
];

export default function HomePage() {
  const [currentTime, setCurrentTime] = useState('');
  const [activeFilter, setActiveFilter] = useState<string | null>(null);

  useEffect(() => {
    const updateTime = () => {
      try {
        const now = new Date();
        const options: Intl.DateTimeFormatOptions = {
          timeZone: 'America/Los_Angeles',
          hour: 'numeric',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        };
        setCurrentTime(now.toLocaleTimeString('en-US', options));
      } catch (e) {
        console.error('Failed to format time:', e);
        setCurrentTime('—');
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleFilterClick = (tag: string) => {
    setActiveFilter((prev) => (prev === tag ? null : tag));
  };

  return (
    <div className="min-h-screen bg-white font-sans cursor-none">
      <CustomCursor />
      <div className="max-w-[1200px] mx-auto px-4 lg:px-8 py-8 lg:py-10">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <Sidebar />

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Breadcrumb / Time */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-1.5 text-[12px] text-notion-secondary">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-home-5-line text-sm"></i>
                </span>
                <span>/</span>
                <span className="text-notion-text font-medium">Annie Ding</span>
                <span>/</span>
                <span>Portfolio</span>
              </div>
              <div className="flex items-center gap-1.5 text-[12px] text-notion-secondary">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-time-line text-sm"></i>
                </span>
                <span>Los Angeles &middot; {currentTime}</span>
              </div>
            </div>

            {/* Hero */}
            <section className="mb-6">
              <h2 className="font-serif text-3xl lg:text-[38px] font-medium text-notion-text leading-tight tracking-tight mb-3">
                Founding UX &amp; Ops Manager @ Raremarq
              </h2>
              <p className="text-[15px] text-notion-secondary leading-relaxed">
                Iterating at a VC-Backed Startup by Launch, Jason Calacanis, and Hyphen Capital, driving{' '}
                <span className="text-notion-accent font-semibold">$300k in ARR</span>!
              </p>
            </section>

            {/* Filterable Tags */}
            <div className="mb-10">
              <div className="flex flex-wrap items-center gap-2 py-3 border-y border-notion-border">
                {FILTER_TAGS.map((tag) => {
                  const isActive = activeFilter === tag;
                  return (
                    <button
                      key={tag}
                      onClick={() => handleFilterClick(tag)}
                      className={`text-[12px] px-3 py-1.5 rounded-full border transition-all duration-200 cursor-pointer whitespace-nowrap ${
                        isActive
                          ? 'bg-notion-accent text-white border-notion-accent'
                          : 'bg-white text-notion-secondary border-notion-border hover:border-notion-accent hover:text-notion-accent'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
                {activeFilter && (
                  <button
                    onClick={() => setActiveFilter(null)}
                    className="text-[11px] text-notion-secondary hover:text-notion-accent transition-colors cursor-pointer flex items-center gap-1 ml-1"
                  >
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-close-line text-xs"></i>
                    </span>
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Project Grid */}
            <ProjectGrid activeFilter={activeFilter} />

            {/* About Section */}
            <AboutSection />

            {/* Footer */}
            <Footer />
          </main>
        </div>
      </div>
    </div>
  );
}
